import React, { FC, useState } from 'react'
import { useIntl } from 'react-intl'
import * as XLSX from 'xlsx';
import fileDownload from 'js-file-download';
import { toast } from 'react-toastify'; 
import { getPaymentDetailsReport, getInvoiceFile } from '../core/_requests'; // Removed uploadInvoice import
import SharedData from '../../common/SharedData';
import {API_URL} from '../../../tenant/apiBase';
import {currencyLabel} from '../../common/currencyLabel'
import {tenantTaxEnabled} from '../../../tenant/tenantState'
import {buildPaymentReportColumnPlan} from './paymentReportColumns'
import {buildTaxBreakdownRows} from './paymentReportTaxBreakdown'


export interface IMonthlyPaymentItem { // Added export keyword
    supplier_payment_id: number; 
    supplier_id: number;
    net: string; // Use 'net' field, type is string
    month: number;
    year: number;
    status: string;
    invoice: string | null;
    date_added: string;
    supplier_name: string;
    month_name: string; // the payment's period ("1–5 September 2025"), or its month
    period_start?: string | null;
    period_end?: string | null;
    status_text: string; 
    rejection_reason?: string | null; // Made optional with ? since it might not always be present
    [key: string]: any; 
}

// Define props including the callback function
type IProps = { 
    item: IMonthlyPaymentItem;
    onOpenUploadModal: (item: IMonthlyPaymentItem) => void; // Callback to open modal in parent
} 

// Rename the component
const PaymentReportTableRow: FC<IProps> = ({ item, onOpenUploadModal }) => { // Destructure the new prop
    const { formatMessage } = useIntl()
    const [isExportLoading, setIsExportLoading] = useState(false);
    // Removed modal state: showUploadModal, isUploadLoading
    const [isViewLoading, setIsViewLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false); // Settlements breakdown toggle
    const { currentUser, language } = SharedData();

    // Modified function to call the prop
    const handleOpenUploadModal = () => {
        onOpenUploadModal(item); // Pass the item data up to the parent
    };
    // Removed handleCloseUploadModal and handleUploadInvoice

    // Function to handle viewing the invoice (remains the same)
    const handleViewInvoice = async () => {
        setIsViewLoading(true);
        try {
            // Fetch the JSON response containing the invoice path
            const response = await getInvoiceFile(item.supplier_payment_id, currentUser?.token, language);
            // Get the path directly from response.data.invoice_path based on updated type
            let serverPath = response.data.invoice_path; 

            if (!serverPath) {
                throw new Error("Invoice path not found in API response.");
            }

            // Replace escaped slashes if present
            serverPath = serverPath.replace(/\\\//g, '/'); 

            // Extract filename from the potentially corrected server path
            const filename = serverPath.split('/').pop(); 
            if (!filename) {
                 throw new Error("Could not extract filename from server path: " + serverPath);
            }

            // Construct the publicly accessible URL based on user feedback
            // Base URL + /lumen/storage/app/ + directory + / + filename
            // Example: https://local-hisbeh.dumyah.com:3011/lumen/storage/app/partner-payment-invoices/payment_1_1745928836.pdf
            const publicUrl = `${API_URL}/lumen/storage/app/partner-payment-invoices/${filename}`;
            // Open the constructed URL in a new tab
            window.open(publicUrl, '_blank', 'noopener,noreferrer');

        } catch (error) {
            toast.error(formatMessage({ id: 'REPORTS.PAYMENT.VIEW_INVOICE.ERROR' }));
        } finally {
            setIsViewLoading(false);
        }
    };


    // Export handler function
    const handleExportReport = async () => {
        setIsExportLoading(true); // Fix: Use renamed state setter
        try {
            const res = await getPaymentDetailsReport(item.supplier_payment_id, item.month, item.year, currentUser?.token, language);
            // Access the different parts of the API response data
            const responsePayload = res.data.data;
            const reportData = responsePayload.transactions;
            // Removed incorrect pickupFees and availabilityFees assignments
            // Cast summaryTotals to 'any' to bypass potential type mismatches from _requests definition
            // This allows using the structure provided in the user's example JSON.
            const summaryTotals: any = responsePayload.totals; 

            // Check if the transactions array is empty or invalid
            if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
                alert(formatMessage({ id: 'REPORTS.PAYMENT.EXPORT.NO_DATA_PERIOD' }, { period: item.month_name })); // Notify user
                setIsExportLoading(false); // Fix: Use renamed state setter
                return; // Stop execution if no data
            }

            // --- Data processing starts only if reportData is valid ---

            // Non-taxable tenant: a column that is purely tax (Tax Type) is
            // dropped entirely; a column whose figure still matters without tax
            // (margin, net value) is kept but its header drops the tax wording.
            // Taxable tenant: every column, original headers — byte-identical to
            // the export before this gate existed. See paymentReportColumns.ts.
            const columnPlan = buildPaymentReportColumnPlan(tenantTaxEnabled());
            const formattedHeaders = columnPlan.headers;

            // Prepare transaction data for Excel sheet using the resolved column
            // plan (Array of Arrays) — `columnPlan.keys` is already filtered and
            // index-aligned with `formattedHeaders`, so headers and row cells
            // stay in the same column order.
            const excelData = reportData.map((row: any) =>
                columnPlan.keys.map((key: string) => {
                    const value = row[key];
                    // Format numbers to 2 decimal places if they are numbers, otherwise use the value or '-'
                    if (typeof value === 'number') {
                        return value.toFixed(2);
                    }
                    return value ?? '-'; // Replace null/undefined with '-'
                })
            );

             // --- Append Summary Totals (Structured like the screenshot) ---
             // Non-taxable tenant: there is no tax rate to break totals down by, so
             // the whole "Totals Breakdown by Tax Rate" section — including the
             // excl./incl. tax summary rows that follow it — is skipped entirely.
             if (tenantTaxEnabled()) {
                 // Columns, fee lines and alignment live in paymentReportTaxBreakdown.ts.
                 excelData.push(...buildTaxBreakdownRows(summaryTotals));
             }
             // --- End of Final Summary ---


            // Create worksheet and workbook using formatted headers and structured data
            const worksheet = XLSX.utils.aoa_to_sheet([formattedHeaders, ...excelData]);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Payment Details');

            // Generate Excel file buffer
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

            // Create a Blob
            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8' });

            // Trigger download using js-file-download
            const periodName = item.period_start && item.period_end
                ? `${item.period_start.slice(0, 10)}_${item.period_end.slice(0, 10)}`
                : `${item.month_name}_${item.year}`;
            fileDownload(blob, `PaymentDetails_${periodName}.xlsx`);

        } catch (error) {
            console.error('Error exporting payment details report:', error);
            alert(formatMessage({ id: 'REPORTS.PAYMENT.EXPORT.ERROR' })); // Notify user of error
        } finally {
            setIsExportLoading(false); // Fix: Use renamed state setter
        }
    };

    // Function to determine which button(s) to show based on status and invoice existence
    const renderActionButton = (status: string, invoiceUrl: string | null) => {
        // Use the actual status field for logic
        const currentStatus = status ? status.toLowerCase().trim() : '';
        const hasInvoice = invoiceUrl && invoiceUrl.trim() !== '';

        if (currentStatus === 'pending_partner_invoice' || currentStatus === 'rejected' || currentStatus== 'pending_reconciliation') {
            return (
                <>
                    {/* Conditionally render View Invoice button */}
                    {hasInvoice && (
                         <button 
                            onClick={handleViewInvoice} // Use the new handler
                            className='btn btn-sm btn-light btn-active-light-info me-2' 
                            disabled={isViewLoading} // Disable while loading
                        >
                            {isViewLoading ? (
                                <>
                                    <span className='spinner-border spinner-border-sm align-middle me-1'></span>
                                    {/* Optional: Add loading text translation */}
                                    Loading... 
                                </>
                            ) : (
                                formatMessage({ id: 'view_invoice' })
                            )}
                        </button>
                    )}
                    {/* Upload/Re-upload button */}
                    <button 
                        className='btn btn-sm btn-light btn-active-light-primary me-2'
                        onClick={handleOpenUploadModal} 
                    >
                        {/* Change text based on invoice existence */}
                        {formatMessage({ id: hasInvoice ? 'reupload_invoice' : 'upload_invoice' })}
                    </button>
                    {/* Export button remains */}
                    <button
                        className='btn btn-sm btn-light btn-active-light-primary'
                        onClick={handleExportReport} // Added onClick handler
                        disabled={isExportLoading} // Fix: Use renamed state
                    >
                        {isExportLoading ? 'Exporting...' : formatMessage({ id: 'export_report' })}
                    </button>
                </>
            );
        }
        // Return null if no specific action is defined for the status
        return null;
    };

    // Parse the 'net' string to a number for formatting
    const netAmount = parseFloat(item.net); 
    // Format amount only if it's a valid number, otherwise show 'N/A' or similar
    const formattedAmount = !isNaN(netAmount) 
        ? new Intl.NumberFormat('en-US').format(netAmount) 
        : 'N/A'; // Display 'N/A' if net is not a valid number string

    // Currency is data-driven: use the row's own currency from the API. Fall
    // back to the JOD label only for legacy rows that predate the currency
    // field (JOD is the store's historical base/accounting currency).
    const currencyString = currencyLabel(item.currency, formatMessage);

    // Settlement breakdown values — populated by the backend's
    // getSupplierPaymentsList formatter (controller lines 63–71). Cast
    // to number; the controller already casts to float but we belt-and-
    // braces in case anything stays string.
    const grossSales = Number(item.total_sales ?? 0);
    const dumyahFees = Number(item.total_dumyah_fees ?? 0);
    const pickupFees = Number(item.total_pickup_fees ?? 0);
    // What was charged for barcode labels printed for this partner, already
    // net of reversals. Zero for nearly everyone, so its tile only appears
    // when there is something to show.
    const barcodePrintingFees = Number(item.total_barcode_printing_fees ?? 0);
    const breakdownAvailable = grossSales > 0 || dumyahFees > 0 || pickupFees > 0 || barcodePrintingFees !== 0;
    // Four tiles fill the row at a quarter each; with the fifth they share it evenly.
    const breakdownTile = barcodePrintingFees !== 0 ? 'col-md' : 'col-md-3';

    const fmt = (n: number) =>
        new Intl.NumberFormat(language === 'ar' ? 'ar-SA' : 'en-US', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        }).format(Number.isFinite(n) ? n : 0);

    return (
        <>
            <tr>
                {/* Month + breakdown toggle. The chevron only renders when
                    the backend supplied the breakdown numbers — older
                    payments may not have them. */}
                <td className='ps-4'>
                    <div className='d-flex align-items-center gap-2'>
                        {breakdownAvailable ? (
                            <button
                                type='button'
                                className='btn btn-icon btn-sm btn-light-primary'
                                onClick={() => setIsExpanded((v) => !v)}
                                aria-expanded={isExpanded}
                                aria-label={formatMessage({ id: 'settlements.row.toggle_breakdown' })}
                                style={{ width: 24, height: 24 }}
                            >
                                <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} fs-8`} aria-hidden='true' />
                            </button>
                        ) : (
                            <span style={{ display: 'inline-block', width: 24 }} />
                        )}
                        <span className='text-gray-800 fw-bold d-block fs-7'>{item.month_name}</span>
                    </div>
                </td>
                {/* Amount */}
                <td className='ps-4'>
                    {/* Display formatted net amount and localized currency */}
                    <span className='text-primary fw-bold d-block fs-7'>{formattedAmount} {currencyString}</span>
                </td>
                {/* Status */}
                <td className='pe-4'>
                    {/* Use status_text */}
                    <span className='text-gray-800 fw-bold d-block fs-7'>{item.status_text}</span>
                    {/* Show rejection reason if it exists */}
                    {item.rejection_reason && (
                        <span className='text-danger fw-normal d-block fs-8 mt-1'>
                            {formatMessage({ id: 'rejection_reason' })}: {item.rejection_reason}
                        </span>
                    )}
                </td>
                {/* Actions */}
                <td className='pe-4'>
                    {/* Pass status and invoice URL to renderActionButton */}
                    {renderActionButton(item.status, item.invoice)}
                </td>
            </tr>

            {/* Inline breakdown — shows the same numbers that were
                previously only available in the Excel export. Suppliers
                no longer have to download a file to see "why is my net
                this amount?" */}
            {isExpanded && breakdownAvailable && (
                <tr className='bg-light'>
                    <td colSpan={4} className='px-4 py-3'>
                        <div className='row g-3'>
                            <div className={breakdownTile}>
                                <div className='text-muted fs-8'>{formatMessage({ id: 'settlements.row.gross_sales' })}</div>
                                <div className='fw-bold fs-6 text-success'>+ {fmt(grossSales)} {currencyString}</div>
                            </div>
                            <div className={breakdownTile}>
                                <div className='text-muted fs-8'>{formatMessage({ id: 'settlements.row.dumyah_fees' })}</div>
                                <div className='fw-bold fs-6 text-danger'>− {fmt(dumyahFees)} {currencyString}</div>
                            </div>
                            <div className={breakdownTile}>
                                <div className='text-muted fs-8'>{formatMessage({ id: 'settlements.row.pickup_fees' })}</div>
                                <div className='fw-bold fs-6 text-danger'>− {fmt(pickupFees)} {currencyString}</div>
                            </div>
                            {barcodePrintingFees !== 0 && (
                                <div className={breakdownTile}>
                                    <div className='text-muted fs-8'>{formatMessage({ id: 'settlements.row.barcode_printing_fees' })}</div>
                                    {/* Negative when a reversal was credited in a later period than the print it undoes. */}
                                    <div className={`fw-bold fs-6 ${barcodePrintingFees < 0 ? 'text-success' : 'text-danger'}`}>
                                        {barcodePrintingFees < 0 ? '+' : '−'} {fmt(Math.abs(barcodePrintingFees))} {currencyString}
                                    </div>
                                </div>
                            )}
                            <div className={`${breakdownTile} border-start ps-3`}>
                                <div className='text-muted fs-8'>{formatMessage({ id: 'settlements.row.net' })}</div>
                                <div className='fw-bold fs-5 text-primary'>{formattedAmount} {currencyString}</div>
                            </div>
                        </div>
                        {item.margin_percentage ? (
                            <div className='text-muted fs-8 mt-2'>
                                {formatMessage({ id: 'settlements.row.commission_pct' }, { pct: Number(item.margin_percentage).toFixed(2) })}
                            </div>
                        ) : null}
                    </td>
                </tr>
            )}
        </>
    );
}

// Update the export
export default PaymentReportTableRow;
